import {BaseModel, DataList, BaseList} from "../data";
import {Utils} from "../common/Utils";
import {Mixin} from "../common/mixwith";


/**
 * This mixin combines a dynamic template, e.g. Handlebars, with a model instance
 * and keep both synchronized. That means when the model will be updated then the
 * template will be automatically re-rendered with the new property values.
 */
export const ListViewMixin = Mixin((superclass) => class extends superclass {

  /**
   *
   * @param config
   */
  constructor(config = {}) {
    super($.extend(true, {
      on: {
        'before:init': '_initView',
        'init': '_afterComponentInit'
      },
      listView: {
        tpl: null,
        itemTpl: null,
        emptyTpl: null,
        wrapper: null,
        container: null,
        showOnInit: true,
        data: null,
        class: DataList,
        options: {},
      },
    }, config));
  }

  /**
   * Initialize a new model instance from the class defined in config
   * or take the model in data config if exists.
   * @private
   */
  _initView() {
    let cfg = this.config.listView;

    this._rendered = false;
    this._itemViewById = {};
    this._itemViewList = [];
    this._hasWrapperView = Utils.isFunction(cfg.tpl);
    this._hasItemView = Utils.isFunction(cfg.itemTpl);
    this._hasEmptyView = Utils.isFunction(cfg.emptyTpl);

    if (!this._hasWrapperView && !this._hasItemView) {
      throw "error: misconfiguration in ListViewMixin. 'tpl' and/or 'itemTpl' must be template function."
    }
    if (this._hasWrapperView && this._hasItemView && !Utils.isString(cfg.container)) {
      throw "error: misconfiguration in ListViewMixin. 'container' must be a DOM selector in 'tpl'"
    }

    /**
     * list instance
     * @type {BaseList}
     */
    this.list = cfg.data;
    if (!this.list) {

      // support array for model class with multiple arguments
      if (Array.isArray(this.config.listView.options)) {
        this.list = new this.config.listView.class(...this.config.listView.options);
      }
      else {
        this.list = new this.config.listView.class(this.config.listView.options);
      }
    }
    if ((!this.list instanceof BaseList)) {
      throw "Invalid config in ModelBinding: data must be a Model class."
    }

    // bind model related functions
    this.list.on('model:change', (list, model, changed) => {
      if (this._rendered && this._hasItemView) {
        this.rerenderItemView(model);
      }
      else {
        this.showView();
      }
    });
    this.list.on('model:sync:loading', this.listItemViewLoading.bind(this));
    this.list.on('model:sync:error', this.listItemViewLoaded.bind(this));
    this.list.on('model:delete:done', (list, model) => {
      if (this._rendered && this._hasItemView) {
        this.removeItemView(model)
      }
      else {
        this.showView();
      }
    });

    // bind list related functions
    this.list.on('sync:loading', this.listViewLoading.bind(this));
    this.list.on('sync:done', (...args) => {
      this.listViewLoaded(false, ...args);

      // TODO: extract in new function (renders items only and replaces old one)
      if (this._rendered && this._hasItemView) {

        this.triggerMethodOn('beforeShowView', ...args);
        if (!args[2] || args[2] && args[2].append !== true) {
          this._hasWrapperView ? this.listViewContainer.empty() : this.detachView();
        }

        if (args[1].length) {
          args[1].forEach((model) => this.addItemView(model));
        }
        else {
          let $html = this.renderEmptyView();
          if (this._hasWrapperView) {
            this.listViewContainer.append($html);
          }
          else {
            this.attachView($html);
          }
        }

        this._rebind();
        this.triggerMethodOn('showView');
      }
      else {
        this.showView();
      }
    });
    this.list.on('sync:error', (...args) => {
      this.listViewLoaded(true, ...args);
    });
    this.list.on('add', (list, model) => {
      if (this._rendered && this._hasItemView) {
        this.addItemView(model);
        this._rebind();
      }
      else {
        this.showView();
      }
    });
    this.list.on('remove', (list, model, index) => {
      if (this._rendered && this._hasItemView) {
        this.removeItemView(model)
      }
      else {
        this.showView();
      }
    });
    this.list.on('reset', (list) => {
      this.showView();
    });
  }

  /**
   * Called after base component has been initialized.
   * Call {@link showView} if showOnInit is set to true.
   * @private
   */
  _afterComponentInit() {
    /**
     * DOM element the view will be appended to
     * @type {jQuery}
     */
    this.listViewWrapper = this.resolveSelector(this.config.listView.wrapper) || this.el;
    this.listViewContainer = null;

    if (this.config.listView.showOnInit) {
      this.showView();
    }
  }

  /**
   * Will be called before the ajax request starts loading.
   */
  listViewLoading() {
    this.listViewWrapper.addClass('loading');
    if (this.listViewContainer) {
      this.listViewContainer.addClass('loading');
    }
  }

  /**
   * To be called when the requests finishes (on success and error).
   * @param {Boolean} isErr - indicates the response is an error
   * @param {BaseList} list - reference to the list instance
   * @param {*} response - error response object or array of BaseModels
   */
  listViewLoaded(isErr, list, response) {
    this.listViewWrapper.removeClass('loading');
    if (this.listViewContainer) {
      this.listViewContainer.removeClass('loading');
    }
  }

  /**
   * To be called if a single model will starts synchronization with backend.
   * Only used if 'itemTpl' is configured.
   * @param {BaseList} list
   * @param {BaseModel} model
   * @param {boolean} isNew
   */
  listItemViewLoading(list, model, isNew) {
    if (this._itemViewById[model.getId()]) {
      this._itemViewById[model.getId()].addClass('loading');
    }
  }

  /**
   * To be called when the requests finished (on success and error).
   * Only used if 'itemTpl' is configured.
   * @param {BaseList} list
   * @param {BaseModel} model
   */
  listItemViewLoaded(list, model) {
    if (this._itemViewById[model.getId()]) {
      this._itemViewById[model.getId()].removeClass('loading');
    }
  }

  /**
   * Handle the whole life cycle from a view:
   *   - renders a template with given model data
   *   - attach the view to the DOM
   *   - delegate ui, events, actions
   */
  showView() {
    if (this.triggerMethodOn('beforeShowView') === false) {
      return;
    }
    let $html = this.renderView();
    if ($html) {
      this.detachView();
      this.attachView($html);
      this._rebind();
      this._rendered = true;
      this.triggerMethodOn('showView');
    }
  }

  /**
   *
   */
  resetView() {
    this.triggerMethodOn('beforeResetView');
    if (this.listViewContainer) {
      this.listViewContainer.empty();
    }
    else {
      this.detachView();
    }
    this._rebind();
    this.triggerMethodOn('resetView');
  }

  /**
   * Passing the list data to the 'tpl' and 'itemTpl' function.
   * @return {jQuery|Array<jQuery>}
   */
  renderView() {
    if (this.triggerMethodOn('beforeRenderView') === false) {
      return false;
    }
    let $html;

    // render per model
    if (this._hasItemView) {
      if (this.list.isEmpty()) {
        $html = this.renderEmptyView();
      }
      else {
        $html = this._itemViewList = this.list.map((model) => this.renderItemView(model));
      }

      // if wrapper view exists -> embed
      if (this._hasWrapperView) {
        let $container = $($.parseHTML(this._renderMainView()));
        let $wrapper = this.listViewContainer = $container.find(this.config.listView.container);
        $wrapper.append($html);
        $html = $container;
      }
    }
    // render main view with 'items' structure
    else {
      let items = this.list.map((model) => model.getData());
      $html = $($.parseHTML(this._renderMainView(items)));
    }

    this.triggerMethodOn('renderView', [$html]);
    return $html;
  }

  /**
   * Passing the given model (from the list) to the itemTpl function,
   * parse the HTML and return a jQuery DOM object.
   * This function will be only used when 'itemTpl' is defined in the configuration.
   * @param {BaseModel} model - odel instance to be embed
   * @return {jQuery}
   */
  renderItemView(model) {
    let data = model.getData();
    if (this.triggerMethodOn('beforeRenderItemView', [model, data]) === false) {
      return;
    }

    let html = this.config.listView.itemTpl(data, this._getTplOptions());
    let $html = $($.parseHTML(html));
    this._itemViewById[model.getId()] = $html;
    return $html;
  }

  /**
   * Renders the empty view template
   * @return {jQuery}
   */
  renderEmptyView() {
    let data = {};
    if (this.triggerMethodOn('beforeRenderEmptyView', [data]) === false) {
      return;
    }

    let html = this.config.listView.emptyTpl(data, this._getTplOptions());
    return $($.parseHTML(html));
  }

  /**
   *
   * @param {BaseModel} model
   */
  rerenderItemView(model) {
    let $item = this._itemViewById[model.getId()];
    let $newItem = this.renderItemView(model);
    if ($item) {
      $item.replaceWith($newItem);
    }
    else if (this.listViewContainer) {
      this.listViewContainer.append($item);
    }
    else {
      this.attachView($item);
    }
    this._rebind();
  }

  /**
   *
   * @param {BaseModel} model
   */
  removeItemView(model) {
    let $item = this._itemViewById[model.getId()];
    if ($item) {
      $item.remove();
    }
  }

  /**
   *
   * @param {BaseModel} model
   */
  addItemView(model) {
    let $item = this.renderItemView(model);
    if (this.listViewContainer) {
      this.listViewContainer.append($item);
    }
    else {
      this.attachView($item);
    }
  }

  /**
   *
   * @param items
   * @return {*}
   * @private
   */
  _renderMainView(items) {
    let data = {
      size: this.list.size(),
      isEmpty: this.list.isEmpty(),
      page: this.list.getPage(),
      pageSize: this.list.getPageSize(),
      totalPages: this.list.getTotalPages(),
      totalSize: this.list.getTotalSize()
    };
    if (items) {
      data.items = items;
    }
    this.triggerMethodOn('beforeRenderMainView', [data]);
    return this.config.listView.tpl(data, this._getTplOptions());
  }

  _getTplOptions() {
    let helpers = {
      __: (key) => this.t(key),
      format: (...args) => this.formatProperty(...args)
    };
    return {
      helpers
    }
  }

  /**
   * Stub reference. To be used as 'format' helper in the template.
   */
  formatProperty(...args) {}

  /**
   * Append the given $html jQuery object to the wrapping element.
   * @param {jQuery} $html - jQuery DOM object
   */
  attachView($html) {
    this.listViewWrapper.append($html);
  }

  /**
   * Remove content from the wrapping element.
   */
  detachView() {
    this.listViewWrapper.empty();
  }

});